import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { ScreenWakeLockHolder } from './screen-wake-lock';
import { VoiceCall, VoiceCallStatus } from './voice-call';
import { onVoicePickSaved } from './voice-pick-saved';
import { primeAudioGesture, ThinkingCue } from './voice-audio';
import { useProductName } from './use-brand';
import { IDLE_PRESENCE, presenceView, reducePresence } from './voice-call-presence';
import type { PresenceView } from './voice-call-presence';
import { capWasReached, reportVoiceLifecycle, voiceEndReason } from './voice-telemetry';

// The model occasionally echoes scaffolding into its speech (and thus its
// transcription). Strip it from the displayed caption so the user never SEES
// markup, regardless of what's spoken.
function sanitizeCaption(text: string): string {
  return text
    .replace(/<\/?voice>/gi, '')
    .replace(/\[[^\]]*\]/g, '')
    .trimStart();
}

export type VoiceCallUiStatus = 'idle' | VoiceCallStatus;

export type Caption = { speaker: 'user' | 'orion'; text: string };

/** Progress of the mid-call hand-off to text. `sent` is left standing — a second hand-off is allowed. */
export type VoiceHandoffState = 'idle' | 'sending' | 'sent' | 'failed';

export interface VoiceCallAudioState {
  inputLevel: number;
  outputLevel: number;
  orionSpeaking: boolean;
}

export interface VoiceCallState {
  status: VoiceCallUiStatus;
  errorDetail: string | null;
  caption: Caption | null;
  muted: boolean;
  /** True while at least one ask is in flight — the assistant is working. */
  working: boolean;
  /** Latest live progress step from the assistant loop, shown (never spoken) while working. */
  thinkingStep: string | null;
  /**
   * The microphone has been silent long enough that the call is probably deaf. Drives the
   * on-call hint; see lib/voice-silence.ts for what "silent" means and when it can't fire.
   */
  micSilent: boolean;
  /**
   * The call has been shrunk to the island so the user can look at whatever they
   * asked for. Presentation only — the session, its audio and its tool calls run
   * on untouched (see lib/voice-call-presence.ts).
   */
  minimized: boolean;
  /** Live view of the call clock: elapsed time and whether the cap is near. */
  timing: PresenceView;
  /**
   * True once the USER has spoken at least once on the current call — i.e. it became a real
   * conversation, not just a connected line the assistant greeted into. Reset on `start`, so a call
   * that failed, never connected, or the user started and immediately ended stays false (the
   * assistant's auto-greeting does NOT flip it).
   *
   * ⚠️ It says "the speech model returned a final user transcript", which is NOT the same as "the
   * user spoke" — the surface's "Listening. Hearing you." runs off the local mic level and can be
   * true while this is false. So it is evidence that a transcript EXISTS to read, and nothing more;
   * it must never be the sole reason to take a screen away from someone (see the teardown rule in
   * MessagesPanel, which now only consults it for a chat the call itself opened).
   */
  producedTurns: boolean;
  start: () => void;
  end: () => void;
  minimize: () => void;
  expand: () => void;
  toggleMuted: () => void;
  /** Live audio controls — each applies to the call in progress. */
  setVolume: (volume: number) => void;
  setInputDevice: (deviceId: string | null) => Promise<void>;
  setOutputDevice: (deviceId: string | null) => Promise<void>;
  /** Play a tone through the selected speaker — "is this the right output?" without a call. */
  testSpeaker: () => Promise<void>;
  /** Poll at animation rate — stable identity, safe outside React state. */
  getAudioState: () => VoiceCallAudioState;
  handoff: VoiceHandoffState;
  /**
   * Hand the call off to text: the assistant writes what has been covered into the chat as a real
   * message, so the user still has it — and can act on it — after hanging up.
   */
  sendToChat: () => void;
}

export interface VoiceCallOptions {
  /** Completed spoken turns, in order — the persistence hook-in point. */
  onTurnTranscript?: (speaker: 'user' | 'orion', text: string) => void;
  /**
   * Resolve a substantive request through the full assistant loop and return
   * its spoken-ready reply. Called when the voice model invokes ask_symphony;
   * the reply is handed back as that call's final tool result. `onThinking`
   * fires per live progress step (shown on screen, never spoken). Throwing
   * yields a spoken apology.
   */
  resolveAsk?: (request: string, onThinking: (step: string) => void) => Promise<string>;
  /**
   * Run one of the model's own tools against the voice tool plane. Returns the harness's
   * classified outcome — already safe to speak.
   */
  callVoiceTool?: (tool: string, params: Record<string, unknown>) => Promise<{ kind: string; result: unknown }>;
  /** The conversation the call belongs to, created on demand if the user has no thread open yet. */
  resolveConversationId?: () => Promise<string | null>;
  /**
   * Run the hand-off to text. Resolves true once the assistant's message is in the conversation.
   * Deliberately not routed back to the voice model — see lib/voice-handoff.ts.
   */
  sendToChat?: () => Promise<boolean>;
}

/**
 * React lifecycle wrapper around VoiceCall. One active call at a time;
 * starting while a call is live is a no-op. `start` must be invoked directly
 * from a click handler — it anchors the autoplay gesture synchronously
 * before any await runs.
 */
export function useVoiceCall(options?: VoiceCallOptions): VoiceCallState {
  const productName = useProductName();
  const [status, setStatus] = useState<VoiceCallUiStatus>('idle');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [caption, setCaption] = useState<Caption | null>(null);
  const [muted, setMuted] = useState(false);
  const [working, setWorking] = useState(false);
  // Separate from `working` on purpose: the indicator stays up for the whole wait, the tick-tock
  // stands down while the agent speaks for itself. VoiceCall owns the derivation.
  const [cueActive, setCueActive] = useState(false);
  const [thinkingStep, setThinkingStep] = useState<string | null>(null);
  const [micSilent, setMicSilent] = useState(false);
  const [producedTurns, setProducedTurns] = useState(false);
  const [handoff, setHandoff] = useState<VoiceHandoffState>('idle');
  const [presence, dispatchPresence] = useReducer(reducePresence, IDLE_PRESENCE);
  // The clock ticks in its own 1Hz state so the surface and the island re-render
  // once a second — never at audio rate, which is what the rAF loop is for.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const callRef = useRef<VoiceCall | null>(null);
  // After a turn completes, the next delta starts a fresh caption rather than
  // appending — otherwise turns concatenate into a run-on (and a slow first
  // turn looks like "half a message").
  const turnEndedRef = useRef(false);
  const productNameRef = useRef(productName);
  productNameRef.current = productName;
  // Refs so `start` stays referentially stable across renders even when the
  // caller passes fresh callbacks each time.
  const onTurnTranscriptRef = useRef(options?.onTurnTranscript);
  onTurnTranscriptRef.current = options?.onTurnTranscript;
  const resolveAskRef = useRef(options?.resolveAsk);
  resolveAskRef.current = options?.resolveAsk;
  const callVoiceToolRef = useRef(options?.callVoiceTool);
  callVoiceToolRef.current = options?.callVoiceTool;
  const resolveConversationIdRef = useRef(options?.resolveConversationId);
  resolveConversationIdRef.current = options?.resolveConversationId;
  const sendToChatRef = useRef(options?.sendToChat);
  sendToChatRef.current = options?.sendToChat;
  // Ask ordering. Non-blocking asks can overlap and can finish out of order;
  // an answer is only "stale" if a LATER ask has already been spoken, because
  // then the user has demonstrably moved on. Stale answers land SILENT (the
  // model keeps the knowledge without hijacking the current exchange).
  const askSeqRef = useRef(0);
  const lastSpokenSeqRef = useRef(-1);
  // Lifecycle telemetry. One call's worth of facts, none of which the server can observe on its
  // own: `callId` stitches the server's own start line to the two reports below, and `endReported`
  // makes the end idempotent — a user hanging up during a failure reaches it twice, and a call
  // counted twice is worse than one counted late.
  const callIdRef = useRef('');
  const startedAtRef = useRef(0);
  const connectedAtRef = useRef(0);
  const turnsRef = useRef(0);
  const userEndedRef = useRef(false);
  const endReportedRef = useRef(false);
  const conversationIdRef = useRef<string | null>(null);
  // The ceiling this call was minted with, published once by `onDurationCap`. Held for the END
  // report to DERIVE whether the call ran into it — see `capWasReached`. Knowing the ceiling is
  // not reaching it, and web is the surface that used to conflate the two.
  const maxCallDurationMsRef = useRef<number | null>(null);

  // Held in a ref, not a useCallback, so `end` and `start` stay genuinely dependency-free. The
  // teardown effect depends on `[end]`, and an `end` whose identity moved would fire that cleanup
  // mid-call and hang the user up the moment the clock ticked (test/voice-call-island.test.ts).
  const reportEndRef = useRef<(status: 'ended' | 'error' | 'idle', detail?: string | null) => void>(() => {});
  reportEndRef.current = (status: 'ended' | 'error' | 'idle', detail?: string | null) => {
    if (!callIdRef.current || endReportedRef.current) return;
    endReportedRef.current = true;
    const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : undefined;
    reportVoiceLifecycle({
      event: 'ended',
      callId: callIdRef.current,
      conversationId: conversationIdRef.current ?? undefined,
      durationMs,
      turns: turnsRef.current,
      reason: voiceEndReason({
        userInitiated: userEndedRef.current,
        status,
        // Derived from how long the call actually ran, which is the rule both phones use. It is
        // NOT a flag set when the ceiling was announced — see `capWasReached`.
        capReached: capWasReached(durationMs, maxCallDurationMsRef.current),
        errorDetail: detail,
      }),
      errorCode: detail ?? undefined,
      // Read off the live call, which is still held here: `end()` nulls the ref only AFTER
      // `call.end()` has emitted 'ended', and a socket death reports while the call is still up.
      ...(callRef.current?.diagnostics ?? {}),
    });
  };

  const end = useCallback(() => {
    // Set BEFORE tearing the call down: `call.end()` emits 'ended' synchronously on a live call, and
    // that handler is what reports — so if this flag lands after, a hang-up is misattributed to the
    // agent and the "who ends calls" split becomes meaningless.
    userEndedRef.current = true;
    reportEndRef.current('idle');
    callRef.current?.end();
    callRef.current = null;
    // Dismiss the overlay unconditionally. After a failure the VoiceCall is
    // already torn down (its end() early-returns and never emits 'ended'), so
    // resetting status here is what actually closes the error screen.
    setStatus('idle');
    setCaption(null);
    setErrorDetail(null);
    setWorking(false);
    setCueActive(false);
    setThinkingStep(null);
    setMicSilent(false);
    dispatchPresence({ type: 'end' });
  }, []);

  const sendToChat = useCallback(() => {
    const run = sendToChatRef.current;
    if (!run) return;
    setHandoff('sending');
    void run()
      .then((ok) => setHandoff(ok ? 'sent' : 'failed'))
      .catch(() => setHandoff('failed'));
  }, []);

  const minimize = useCallback(() => dispatchPresence({ type: 'minimize' }), []);
  const expand = useCallback(() => dispatchPresence({ type: 'expand' }), []);

  const start = useCallback(() => {
    if (callRef.current) return;
    primeAudioGesture();
    setErrorDetail(null);
    setCaption(null);
    setMuted(false);
    setWorking(false);
    setCueActive(false);
    setThinkingStep(null);
    setMicSilent(false);
    setHandoff('idle');
    setProducedTurns(false);
    turnEndedRef.current = false;
    askSeqRef.current = 0;
    lastSpokenSeqRef.current = -1;
    startedAtRef.current = Date.now();
    connectedAtRef.current = 0;
    turnsRef.current = 0;
    maxCallDurationMsRef.current = null;
    userEndedRef.current = false;
    endReportedRef.current = false;
    conversationIdRef.current = null;
    dispatchPresence({ type: 'start', at: Date.now() });
    setNowMs(Date.now());
    const call = new VoiceCall(
      {
        onStatusChange: (next, detail) => {
          setStatus(next);
          dispatchPresence({ type: 'status', status: next });
          // Audio is actually flowing. The gap from tap to here is the number that would have made
          // the 40s-connect regression obvious from production rather than from a bug report.
          if (next === 'live' && !connectedAtRef.current) {
            connectedAtRef.current = Date.now();
            reportVoiceLifecycle({
              event: 'connected',
              callId: callIdRef.current,
              conversationId: conversationIdRef.current ?? undefined,
              connectMs: connectedAtRef.current - startedAtRef.current,
            });
          }
          if (next === 'error') setErrorDetail(detail ?? null);
          if (next === 'ended' || next === 'error') {
            reportEndRef.current(next, detail);
            callRef.current = null;
            // The wait is over whichever way this went. Without this, `working` stays true and the
            // thinking cue keeps ticking behind the "call failed" surface until the user dismisses
            // it — the app pretending to work on a call that is gone. Native does the same.
            setWorking(false);
            setCueActive(false);
            setThinkingStep(null);
          }
        },
        onDurationCap: (maxCallDurationMs) => {
          // This fires ONCE PER MINT, at the START of the call — it publishes the ceiling so the
          // clock can say "ends in about N min". It does NOT mean the ceiling was reached, and
          // treating it as such is what made every successfully-minted web call log
          // `reason: 'cap_reached'` (which outranks `user_ended`), three-second hang-ups included.
          // Record the ceiling; the end report DERIVES whether the call ran into it.
          maxCallDurationMsRef.current = maxCallDurationMs;
          dispatchPresence({ type: 'cap', maxCallDurationMs });
        },
        onTranscriptDelta: (speaker, text) => {
          setCaption((prev) => {
            const fresh = turnEndedRef.current || !prev || prev.speaker !== speaker;
            turnEndedRef.current = false;
            const combined = fresh ? text : prev.text + text;
            return { speaker, text: sanitizeCaption(combined) };
          });
        },
        onTurnTranscript: (speaker, text) => {
          turnsRef.current += 1;
          // A real conversation needs the USER to have spoken. The assistant greets ("Hey, I'm here
          // — what's on your mind?") the instant a call connects, so counting that greeting would
          // make every call that merely CONNECTED read as productive — a call the user starts and
          // immediately ends would leave its greeting on screen instead of dismissing.
          if (speaker === 'user') setProducedTurns(true);
          onTurnTranscriptRef.current?.(speaker, text);
        },
        onTurnComplete: () => {
          turnEndedRef.current = true;
        },
        onMutedChange: (next) => {
          setMuted(next);
        },
        onMicSilentChange: (silent) => {
          setMicSilent(silent);
        },
        onWorkingChange: (inFlight) => {
          setWorking(inFlight > 0);
          if (inFlight === 0) setThinkingStep(null);
        },
        onThinkingCueChange: setCueActive,
        resolveConversationId: async () => {
          const id = (await resolveConversationIdRef.current?.()) ?? null;
          // Remembered purely so the lifecycle reports can be correlated with the conversation the
          // call belongs to; the call itself already has the id from this same resolution.
          conversationIdRef.current = id;
          return id;
        },
        onVoiceTool: (callId, tool, params) => {
          const run = callVoiceToolRef.current;
          const active = callRef.current;
          if (!run || !active) {
            active?.deliverToolResult(callId, { kind: 'failed', result: "I can't reach that right now." });
            return;
          }
          void run(tool, params)
            .then((outcome) => active.deliverToolResult(callId, outcome))
            .catch(() =>
              active.deliverToolResult(callId, {
                kind: 'failed',
                result: "That didn't go through — I'll try again if you want.",
              }),
            );
        },
        onProfileFact: (tool, params) => {
          // Fire-and-forget through the same relay the model's own tools use, so no surface needs a
          // new endpoint for the write to stick. A failure is logged and dropped: the model already
          // said "got it" and has already adjusted for this call, so surfacing an error here would
          // interrupt a call over something the user cannot act on.
          void callVoiceToolRef.current?.(tool, params).catch((err) => {
            console.warn('[voice] could not persist what the call just learned', err);
          });
        },
        onAsk: (callId, request) => {
          const resolve = resolveAskRef.current;
          const active = callRef.current;
          if (!resolve || !active) {
            active?.deliverAnswer(callId, "Sorry, I can't reach the assistant right now.");
            return;
          }
          const seq = askSeqRef.current++;
          setThinkingStep(null);
          const settle = (reply: string) => {
            if (!active.hasPendingAsk(callId)) return;
            const stale = seq < lastSpokenSeqRef.current;
            if (!stale) lastSpokenSeqRef.current = seq;
            active.deliverAnswer(callId, reply, { stale });
          };
          void resolve(request, (step) => setThinkingStep(step))
            .then(settle)
            .catch(() => settle('Sorry, I hit a snag reaching the assistant — try again in a moment.'));
        },
      },
      productNameRef.current,
    );
    callRef.current = call;
    // The call's OWN id, not a second one: it is what the token mint already sends, so the server's
    // `[voice] call start` line and these reports land on the same key.
    callIdRef.current = call.callId;
    void call.start();
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      callRef.current?.setMuted(!prev);
      return !prev;
    });
  }, []);

  const setVolume = useCallback((volume: number) => {
    callRef.current?.setVolume(volume);
  }, []);

  const setInputDevice = useCallback(async (deviceId: string | null) => {
    await callRef.current?.setInputDevice(deviceId);
  }, []);

  const setOutputDevice = useCallback(async (deviceId: string | null) => {
    await callRef.current?.setOutputDevice(deviceId);
  }, []);

  const testSpeaker = useCallback(async () => {
    await callRef.current?.testSpeaker();
  }, []);

  const getAudioState = useCallback(
    (): VoiceCallAudioState => callRef.current?.getAudioState() ?? { inputLevel: 0, outputLevel: 0, orionSpeaking: false },
    [],
  );

  // Deterministic "working" audio cue: a clock tick-tock fills the otherwise-silent tool wait.
  // Driven by the call's own cue signal — in flight AND the agent not talking — so the tick never
  // doubles up on the agent's own "one sec". Torn down with the call.
  const cueRef = useRef<ThinkingCue | null>(null);
  if (!cueRef.current) cueRef.current = new ThinkingCue();
  useEffect(() => {
    if (cueActive) cueRef.current?.start();
    else cueRef.current?.stop();
  }, [cueActive]);

  // A voice saved on the Maestro page while this call is up (in-call Info ▸ Customize voice) reaches
  // the call now, not the next one. The call minimizes to the island to let the user get there, and is
  // still this hook's call; `applySavedVoice` ignores a call that has no session yet or has ended.
  useEffect(() => onVoicePickSaved(() => callRef.current?.applySavedVoice()), []);

  // The call clock. A call minimized to the island is still this hook's call, so
  // the tick is owned here rather than by either surface.
  const running = presence.startedAt != null;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [running]);

  // Hold the screen for as long as a call is LIVE — not for as long as the call surface is on top.
  // A minimized call is still a call: the user is listening and talking with the chat collapsed, and a
  // display timeout ends that just as rudely. Native draws the same line (its keep-screen-on is keyed
  // on the call, not the screen), and this hook already owns the clock for exactly that reason.
  //
  // Web gets the screen half ONLY. Browsers expose no proximity sensor, so the phone-to-ear blanking
  // the native apps do has no counterpart here — see `native-apps/CLAUDE.md`.
  const wakeLockRef = useRef<ScreenWakeLockHolder | null>(null);
  const callLive = status === 'connecting' || status === 'live';
  useEffect(() => {
    if (!callLive) return;
    const holder = wakeLockRef.current ?? new ScreenWakeLockHolder();
    wakeLockRef.current = holder;
    void holder.acquire();
    // The browser silently drops the sentinel whenever the page is hidden. Without re-taking it on
    // the way back, a call that survives one tab switch lets the display time out for the rest of
    // its life — the failure this listener exists to prevent, and the reason it is not optional.
    const onVisibility = () => void holder.onVisibilityChange();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      void holder.release();
    };
  }, [callLive]);

  // Last-resort teardown: a call with no component left to own it would keep the
  // microphone open forever. This is NOT how minimizing works — `<ChatSurface>`
  // stays mounted for the whole session (see FloatingChat), so navigating away or
  // shrinking to the island never reaches here and the call rides straight
  // through. Only a real unmount does.
  useEffect(() => {
    return () => {
      cueRef.current?.close();
      end();
    };
  }, [end]);

  return {
    status,
    errorDetail,
    caption,
    muted,
    working,
    thinkingStep,
    micSilent,
    minimized: presence.minimized,
    timing: presenceView(presence, nowMs),
    producedTurns,
    start,
    end,
    minimize,
    expand,
    toggleMuted,
    setVolume,
    setInputDevice,
    setOutputDevice,
    testSpeaker,
    getAudioState,
    handoff,
    sendToChat,
  };
}
